import React, { useEffect, useState } from 'react';
import { Mail, MessageSquare, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';

const SMS_LIMIT = 480;
const OPENERS = {
  sms: [
    ['Looking into it', (t, me) => `Hi ${first(t.installer_name)}, ${me} here from Ox Resin support. We've got your request (${t.reference}) and are looking into it now — I'll update you shortly.`],
    ['Need more info', (t, me) => `Hi ${first(t.installer_name)}, ${me} from Ox Resin support re ${t.reference}. Could you send us a bit more detail or a photo so we can sort this quickly? Thanks!`],
    ['Resolved', (t, me) => `Hi ${first(t.installer_name)}, ${me} from Ox Resin. Your request ${t.reference} has been sorted — shout if anything else crops up. Thanks!`],
  ],
  email: [
    ['Looking into it', (t, me) => `Hi ${first(t.installer_name)},\n\nThanks for getting in touch. We've received your request (${t.reference} — ${t.title || ''}) and are looking into it now. I'll come back to you as soon as I have an update.\n\nKind regards,\n${me}\nOx Resin support`],
    ['Need more info', (t, me) => `Hi ${first(t.installer_name)},\n\nThanks for your request (${t.reference}). To get this sorted quickly, could you reply with a little more detail — and a photo if it's on site?\n\nKind regards,\n${me}\nOx Resin support`],
    ['Resolved', (t, me) => `Hi ${first(t.installer_name)},\n\nJust to confirm your request (${t.reference}) has now been resolved. If anything else comes up, reply to this email and we'll pick it straight up.\n\nKind regards,\n${me}\nOx Resin support`],
  ],
};
const first = (n) => (n || '').trim().split(/\s+/)[0] || 'there';

/** Text or email the person who raised a support ticket; the message is logged on the ticket. */
export function SupportContactModal({ open, onClose, ticket, channel: initialChannel = 'sms', staffName, onSent }) {
  const [channel, setChannel] = useState(initialChannel);
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !ticket) return;
    setChannel(initialChannel);
    setBody('');
    setSubject(`Re: ${ticket.reference || 'your support request'} — ${ticket.title || ''}`.trim().replace(/ — $/, ''));
  }, [open, ticket, initialChannel]);
  useEffect(() => { if (ticket) setTo(channel === 'sms' ? (ticket.installer_phone || '') : (ticket.installer_email || '')); }, [channel, ticket]);

  if (!ticket) return null;
  const me = staffName || 'The team';
  const canSend = !busy && to.trim() && body.trim() && (channel === 'email' || body.length <= SMS_LIMIT);

  const send = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticket.id}/contact`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ channel, to: to.trim(), subject: channel === 'email' ? subject : undefined, body: body.trim() }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Send failed');
      toast.success(channel === 'sms' ? `Text sent to ${to.trim()}` : `Email sent to ${to.trim()}`);
      onSent?.(d.note); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="support-contact-modal">
        <DialogHeader>
          <DialogTitle>Contact {ticket.installer_name || 'the requester'}</DialogTitle>
          <DialogDescription>{ticket.reference}{ticket.title ? ` · ${ticket.title}` : ''} — the message is saved on the ticket.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="inline-flex rounded-lg border bg-gray-50 p-0.5" role="tablist">
            {[['sms', 'Text (SMS)', MessageSquare], ['email', 'Email', Mail]].map(([k, l, Icon]) => (
              <button key={k} type="button" role="tab" aria-selected={channel === k} onClick={() => setChannel(k)} data-testid={`support-contact-tab-${k}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md ${channel === k ? 'bg-slate-900 text-white shadow' : 'text-gray-600 hover:text-gray-900'}`}><Icon className="w-4 h-4" /> {l}</button>
            ))}
          </div>
          <div>
            <Label htmlFor="sc-to">{channel === 'sms' ? 'Mobile number' : 'Email address'}</Label>
            <Input id="sc-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder={channel === 'sms' ? '07…' : 'name@example.com'} className="bg-white" data-testid="support-contact-to" />
            {!to && <p className="text-[11px] text-amber-700 mt-1">No {channel === 'sms' ? 'phone number' : 'email'} on this ticket — type one in.</p>}
          </div>
          {channel === 'email' && (
            <div><Label htmlFor="sc-subject">Subject</Label><Input id="sc-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-white" data-testid="support-contact-subject" /></div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="sc-body">Message</Label>
              <div className="flex gap-1">{OPENERS[channel].map(([l, fn]) => <button key={l} type="button" onClick={() => setBody(fn(ticket, me))} className="text-[11px] px-2 py-0.5 rounded-full border bg-white hover:bg-gray-50" data-testid={`support-contact-opener-${l.replace(/\s+/g, '-').toLowerCase()}`}>{l}</button>)}</div>
            </div>
            <Textarea id="sc-body" rows={channel === 'sms' ? 4 : 8} value={body} onChange={(e) => setBody(e.target.value)} placeholder={channel === 'sms' ? 'Keep it short — this goes as a text message.' : 'Write your reply…'} className="bg-white" data-testid="support-contact-body" />
            {channel === 'sms' && <p className={`text-[11px] mt-1 ${body.length > SMS_LIMIT ? 'text-rose-600 font-semibold' : 'text-gray-500'}`} data-testid="support-contact-count">{body.length}/{SMS_LIMIT} characters{body.length > 160 ? ` · ${Math.ceil(body.length / 153)} SMS parts` : ''}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={send} disabled={!canSend} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="support-contact-send">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}{channel === 'sms' ? 'Send text' : 'Send email'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
